import classNames from 'classnames';
import React from 'react';
import Breadcrumbs from '../components/Breadcrumbs';
import PostDetailPreviewPopOver from '../post/PostDetailPreviewPopOver';
import type { SlimPostWithTopics } from '../post/useAllSlimPosts';
import nonEmpty from '../utils/nonEmpty';
import Link from '../utils/router/Link';
import postPath from '../utils/url/postPath';
import TemplateIcon from './TemplateIcon';
import * as styles from './TemplateRow.module.scss';
import type { LibraryTemplate } from './useTemplatesSections';

const getTemplateBreadcrumbsItems = (
  templateTopic: SlimPostWithTopics['topics'][number] | null,
  currentTopicId?: string,
) => {
  if (!templateTopic) {
    return [];
  }

  const currentTopicInAncestorsIndex = currentTopicId
    ? (templateTopic.ancestors?.findIndex(
        (ancestor) => ancestor?.id === currentTopicId,
      ) ?? -1)
    : (templateTopic.ancestors?.length ?? -1);

  if (currentTopicInAncestorsIndex === -1) {
    return [];
  }

  // Take ancestors only up to current topic (excluding).
  const ancestorsToBreadcrumbs =
    templateTopic.ancestors
      ?.slice(0, currentTopicInAncestorsIndex)
      .toReversed() ?? [];

  return [...ancestorsToBreadcrumbs, templateTopic]
    .map((topic) => (topic?.name ? { name: topic.name } : null))
    .filter(nonEmpty);
};

const getLinkProps = (
  template: SlimPostWithTopics | LibraryTemplate,
): React.ComponentProps<typeof Link> =>
  'id' in template
    ? { to: postPath(template) }
    : { to: template.link, newTab: true };

type TemplateRowProps = {
  currentTopicId?: string;
  showHoverCard?: boolean;
  padding?: 'regular' | 'small';
  hoverCardPlacement?: React.ComponentProps<
    typeof PostDetailPreviewPopOver
  >['placement'];
  template: SlimPostWithTopics | LibraryTemplate;
};

const TemplateRow = ({
  template,
  currentTopicId,
  showHoverCard = false,
  padding = 'regular',
  hoverCardPlacement = 'left',
}: TemplateRowProps) => {
  const topic = (() => {
    if ('topics' in template && template.topics.length > 0) {
      return template.topics[0] ?? null;
    }

    return null;
  })();

  const breadcrumbsItems = getTemplateBreadcrumbsItems(topic, currentTopicId);

  const row = (
    <Link
      className={classNames(styles.row, {
        [styles.small]: padding === 'small',
      })}
      {...getLinkProps(template)}
    >
      <TemplateIcon />
      <div className={styles.content}>
        <div className={styles.breadcrumbs}>
          <Breadcrumbs items={breadcrumbsItems} truncate />
        </div>
        <div
          className={classNames(styles.title, {
            [styles.titleOneLine]: breadcrumbsItems.length > 0,
            [styles.titleTwoLines]: breadcrumbsItems.length === 0,
          })}
        >
          {template.title}
        </div>
      </div>
    </Link>
  );

  if ('id' in template && showHoverCard) {
    return (
      <PostDetailPreviewPopOver
        postId={template.id}
        placement={hoverCardPlacement}
      >
        {row}
      </PostDetailPreviewPopOver>
    );
  }

  return row;
};

export default TemplateRow;
